import { useState } from 'react'
import { FolderOpen, Cpu, Box, ClipboardList, ChevronRight, ChevronDown, Loader2, CheckCircle2, ExternalLink } from 'lucide-react'
import { aiApi, type ProjectProposal, type CreateFromProposalResult } from '../../services/aiApi'

interface Props {
  proposal: ProjectProposal
  onCreated: (result: CreateFromProposalResult) => void
  onCancel: () => void
}

export function ProjectProposalCard({ proposal, onCreated, onCancel }: Props) {
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(
    new Set(proposal.systems.map((s) => s.code))
  )
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function toggleSystem(code: string) {
    setExpandedSystems((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  async function handleCreate() {
    setIsCreating(true)
    setError(null)
    try {
      const result = await aiApi.createFromProposal(proposal)
      onCreated(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project')
    } finally {
      setIsCreating(false)
    }
  }

  const totalEquipment = proposal.systems.reduce(
    (sum, s) => sum + (s.equipment?.length || 0), 0
  )

  return (
    <div className="proposal-card">
      <div className="proposal-header">
        <FolderOpen size={18} />
        <div className="proposal-header-info">
          <span className="proposal-project-name">{proposal.project.projectName}</span>
          <span className="proposal-project-number">{proposal.project.projectNumber}</span>
        </div>
      </div>

      {(proposal.project.clientName || proposal.project.location) && (
        <div className="proposal-meta">
          {proposal.project.clientName && <span>Client: {proposal.project.clientName}</span>}
          {proposal.project.location && <span>Location: {proposal.project.location}</span>}
        </div>
      )}

      {proposal.project.description && (
        <p className="proposal-description">{proposal.project.description}</p>
      )}

      <div className="proposal-stats">
        <span className="proposal-stat">
          <Cpu size={14} /> {proposal.systems.length} systems
        </span>
        <span className="proposal-stat">
          <Box size={14} /> {totalEquipment} equipment
        </span>
        <span className="proposal-stat">
          <ClipboardList size={14} /> {proposal.suggestedChecklists?.length || 0} checklists
        </span>
      </div>

      <div className="proposal-systems">
        {proposal.systems.filter((s) => !s.parentCode).map((sys) => (
          <SystemNode
            key={sys.code}
            system={sys}
            allSystems={proposal.systems}
            expanded={expandedSystems}
            onToggle={toggleSystem}
          />
        ))}
      </div>

      {(proposal.suggestedChecklists?.length ?? 0) > 0 && (
        <div className="proposal-checklists">
          <div className="proposal-section-title">Suggested Checklists</div>
          {proposal.suggestedChecklists.map((cl, i) => (
            <div key={i} className="proposal-checklist-item">
              <span className={`proposal-checklist-type type-${cl.type.toLowerCase()}`}>{cl.type}</span>
              <span className="proposal-checklist-name">{cl.name}</span>
            </div>
          ))}
        </div>
      )}

      {error && <div className="proposal-error">{error}</div>}

      <div className="proposal-actions">
        <button className="btn btn-ghost" onClick={onCancel} disabled={isCreating}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleCreate} disabled={isCreating}>
          {isCreating ? (
            <>
              <Loader2 size={16} className="spin" /> Creating...
            </>
          ) : (
            <>
              <CheckCircle2 size={16} /> Create Project
            </>
          )}
        </button>
      </div>
    </div>
  )
}

function SystemNode({
  system,
  allSystems,
  expanded,
  onToggle,
  depth = 0,
}: {
  system: ProjectProposal['systems'][0]
  allSystems: ProjectProposal['systems']
  expanded: Set<string>
  onToggle: (code: string) => void
  depth?: number
}) {
  const children = allSystems.filter((s) => s.parentCode === system.code)
  const isExpanded = expanded.has(system.code)
  const hasContent = (system.equipment?.length ?? 0) > 0 || children.length > 0

  return (
    <div className="proposal-system-node" style={{ marginLeft: depth * 16 }}>
      <div className="proposal-system-header" onClick={() => hasContent && onToggle(system.code)}>
        {hasContent ? (
          isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
        ) : (
          <span style={{ width: 14 }} />
        )}
        <span className="proposal-system-code">[{system.code}]</span>
        <span className="proposal-system-name">{system.name}</span>
        {system.systemType && (
          <span className="proposal-system-type">{system.systemType}</span>
        )}
      </div>

      {isExpanded && (
        <>
          {(system.equipment || []).map((eq, i) => (
            <div key={i} className="proposal-equipment-item" style={{ marginLeft: (depth + 1) * 16 + 14 }}>
              <Box size={12} />
              <span className="proposal-equip-tag">{eq.tag}</span>
              <span className="proposal-equip-name">{eq.name}</span>
            </div>
          ))}
          {children.map((child) => (
            <SystemNode
              key={child.code}
              system={child}
              allSystems={allSystems}
              expanded={expanded}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </>
      )}
    </div>
  )
}

export function ProposalCreatedMessage({ result }: { result: CreateFromProposalResult }) {
  return (
    <div className="proposal-created">
      <CheckCircle2 size={16} />
      <div>
        <strong>Project created successfully!</strong>
        <div className="proposal-created-stats">
          {result.systemCount} systems, {result.equipmentCount} equipment, {result.templateCount} checklist templates
        </div>
        <a href={`/projects/${result.projectId}`} className="proposal-created-link">
          <ExternalLink size={14} /> Open Project
        </a>
      </div>
    </div>
  )
}
